import { useState, useEffect } from 'react';
import { useAllOrgUnits, useListOrgUnits } from '@api/hooks/useOrgUnits';
import { useListUsers } from '@api/hooks/useUsers';
import { apiClient } from '@api/client';
import type { OrgUnit, AdminUser, OrgMember } from '@schemas/api';
import { ReactFlow, useNodesState, useEdgesState, MarkerType, Position as HandlePosition, Handle } from '@xyflow/react';
import { Background } from '@xyflow/react';
import { Controls } from '@xyflow/react';
import { MiniMap } from '@xyflow/react';
import type { Node, Edge } from '@xyflow/react';
import * as dagre from 'dagre';
import { useTheme } from 'next-themes';
import Button from '@components/common/Button';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@components/common/Dialog';

// Styles for React Flow 12
import '@xyflow/react/dist/style.css';

type TreeMember = OrgMember & {
  user?: AdminUser;
};

type TreeOrgUnit = OrgUnit & {
  children: TreeOrgUnit[];
  members: TreeMember[];
  manager?: AdminUser;
};

type TreeNode = TreeOrgUnit | TreeMember;

const isTreeOrgUnit = (node: TreeNode): node is TreeOrgUnit => {
  return 'unit_type' in node;
};

const isTreeMember = (node: TreeNode): node is TreeMember => {
  return 'user_id' in node && 'membership_type' in node;
};

interface OrgUnitNodeData extends Record<string, unknown> {
  node: TreeNode;
  isCollapsed?: boolean;
  onToggleCollapse?: (unitId: string) => void;
  onUnitClick?: (unit: TreeOrgUnit) => void;
}

// Custom Node component for org units and members
const OrgUnitNode = ({ data }: { data: OrgUnitNodeData }) => {
  const node = data.node;
  const isMember = isTreeMember(node);
  const unit = isMember ? undefined : (node as TreeOrgUnit);
  const member = isMember ? (node as TreeMember) : undefined;

  // Get initials based on node type
  let initials = '—';
  if (isMember && member?.user) {
    initials = `${member.user.first_name[0]}${member.user.last_name?.[0] || ''}`;
  } else if (!isMember && unit?.manager) {
    initials = `${unit.manager.first_name[0]}${unit.manager.last_name?.[0] || ''}`;
  }

  const hasChildren = !isMember && unit && unit.children && unit.children.length > 0;
  const isVacant = !isMember && unit && !unit.manager_id;
  const memberDisplayName = isMember && member
    ? member.user
      ? `${member.user.first_name} ${member.user.last_name}`
      : member.user_id
    : '';

  if (isMember && member) {
    return (
      <>
        <div className="w-full px-4 py-2 min-w-[200px] shadow-lg rounded-lg text-left transition-colors border-2 border-solid bg-blue-50 dark:bg-blue-900/20">
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-700 font-medium flex items-center justify-center transition-colors cursor-default"
              disabled
            >
              {initials}
            </button>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium truncate text-sm">
                {memberDisplayName}
              </h3>
              <p className="text-xs truncate text-muted-foreground">
                {member.membership_type}
              </p>
            </div>
          </div>
        </div>
        <Handle type="target" position={HandlePosition.Top} isConnectable={false} />
      </>
    );
  }

  if (!unit) return null;

  return (
    <>
      <div
        onClick={() => hasChildren && data.onToggleCollapse?.(unit.id)}
        onKeyDown={(e) => {
          if (hasChildren && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            data.onToggleCollapse?.(unit.id);
          }
        }}
        role="button"
        tabIndex={hasChildren ? 0 : -1}
        className={`w-full px-4 py-2 min-w-[240px] shadow-lg rounded-lg text-left transition-colors ${
          isVacant
            ? 'border-2 border-dashed border-muted-foreground bg-muted/20 text-muted-foreground'
            : 'border-2 border-solid bg-card'
        } ${
          hasChildren ? 'cursor-pointer hover:border-primary' : 'cursor-default'
        }`}
        title={hasChildren ? (data.isCollapsed ? 'Expand' : 'Collapse') : 'No sub-units'}
      >
        <div className="flex items-center gap-3">
          {hasChildren && (
            <div className="flex-shrink-0">
              {data.isCollapsed ? (
                <ChevronRight className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </div>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              data.onUnitClick?.(unit);
            }}
            className={`flex items-center justify-center font-medium flex-shrink-0 transition-colors ${
              isVacant
                ? 'w-8 h-8 rounded-full bg-muted text-muted-foreground cursor-default'
                : 'w-8 h-8 rounded-full bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground cursor-pointer'
            }`}
            title={unit.manager ? 'Click to view details' : 'No manager assigned'}
          >
            {initials}
          </button>
          <div className="flex-1 min-w-0">
            <h3 className={`font-medium truncate ${isVacant ? 'text-muted-foreground' : ''}`}>
              {unit.name}
            </h3>
            <p
              className={`text-xs truncate ${
                isVacant
                  ? 'text-muted-foreground/60 italic'
                  : 'text-muted-foreground'
              }`}
            >
              <span className="capitalize">{unit.unit_type}</span>
              {unit.manager && ` • ${unit.manager.first_name} ${unit.manager.last_name}`}
            </p>
          </div>
        </div>
      </div>
      <Handle type="target" position={HandlePosition.Top} isConnectable={false} />
      <Handle type="source" position={HandlePosition.Bottom} isConnectable={false} />
    </>
  );
};

// Build organization tree from units
const buildOrgTree = (
  units: OrgUnit[] = [],
  userMap: Map<string, AdminUser> = new Map(),
  membersMap: Map<string, TreeMember[]> = new Map()
): TreeOrgUnit[] => {
  // Create node map with users and members
  const nodeMap = new Map<string, TreeOrgUnit>();

  units.forEach((unit) => {
    const treeUnit: TreeOrgUnit = {
      ...unit,
      children: [],
      members: membersMap.get(unit.id) || [],
      manager: unit.manager_id ? userMap.get(unit.manager_id) : undefined,
    };
    nodeMap.set(unit.id, treeUnit);
  });

  // Build hierarchy by connecting parents and children
  nodeMap.forEach((node) => {
    if (node.parent_id) {
      const parent = nodeMap.get(node.parent_id);
      if (parent) {
        parent.children.push(node);
      } else {
        console.warn(`Parent unit ${node.parent_id} not found for ${node.name}`);
      }
    }
  });

  // Get top-level units (no parent)
  const topLevel = Array.from(nodeMap.values()).filter((node) => !node.parent_id);

  // Sort all children alphabetically
  const sortChildren = (node: TreeOrgUnit): void => {
    node.children.sort((a: TreeOrgUnit, b: TreeOrgUnit) =>
      a.name.localeCompare(b.name)
    );
    node.children.forEach(sortChildren);
  };

  topLevel.sort((a, b) => a.name.localeCompare(b.name));
  topLevel.forEach(sortChildren);

  return topLevel;
};

// Convert tree to ReactFlow nodes and edges
const createNodesAndEdges = (
  tree: TreeOrgUnit[],
  parentX: number = 0,
  parentY: number = 0,
  level: number = 0,
  nodes: Node<OrgUnitNodeData>[] = [],
  edges: Edge[] = []
): { nodes: Node<OrgUnitNodeData>[]; edges: Edge[] } => {
  const horizontalSpacing = 350;
  const verticalSpacing = 150;

  tree.forEach((unit, index) => {
    const x = parentX + (index - (tree.length - 1) / 2) * horizontalSpacing;
    const y = parentY + verticalSpacing;

    nodes.push({
      id: unit.id,
      type: 'orgUnitNode',
      position: { x, y },
      data: { node: unit },
      connectable: false,
      draggable: false,
    });

    // Add member nodes and edges
    const memberYOffset = 120;
    const memberXSpacing = 200;
    unit.members.forEach((member, memberIndex) => {
      const memberId = `member-${unit.id}-${member.id}`;
      const memberX = x + (memberIndex - (unit.members.length - 1) / 2) * memberXSpacing;
      const memberY = y + memberYOffset;

      nodes.push({
        id: memberId,
        type: 'orgUnitNode',
        position: { x: memberX, y: memberY },
        data: { node: member },
        connectable: false,
        draggable: false,
      });

      edges.push({
        id: `${unit.id}-${memberId}`,
        source: unit.id,
        target: memberId,
        type: 'smoothstep',
        style: { stroke: '#9CA3AF' },
        markerEnd: {
          type: MarkerType.Arrow,
        },
      });
    });

    // Add edges to children units
    unit.children.forEach((child: TreeOrgUnit) => {
      edges.push({
        id: `${unit.id}-${child.id}`,
        source: unit.id,
        target: child.id,
        type: 'smoothstep',
        style: { stroke: '#374151' },
        markerEnd: {
          type: MarkerType.Arrow,
        },
      });
    });

    if (unit.children.length > 0) {
      createNodesAndEdges(unit.children, x, y, level + 1, nodes, edges);
    }
  });

  return { nodes, edges };
};

// Layout the graph using dagre
const getLayoutedElements = (
  nodes: Node<OrgUnitNodeData>[],
  edges: Edge[],
  direction: string = 'TB'
): { nodes: Node<OrgUnitNodeData>[]; edges: Edge[] } => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction, nodesep: 100, ranksep: 120 });

  const nodeWidth = 280;
  const nodeHeight = 100;

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  return {
    nodes: nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - nodeWidth / 2,
          y: nodeWithPosition.y - nodeHeight / 2,
        },
      };
    }),
    edges,
  };
};

// Node types configuration
const nodeTypes = {
  orgUnitNode: OrgUnitNode,
};

interface OrgStructureTreeProps {
  hierarchyId?: string | null;
}

// Main organizational structure tree component
export default function OrgStructureTree({ hierarchyId }: OrgStructureTreeProps) {
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());
  const [selectedUnit, setSelectedUnit] = useState<TreeOrgUnit | null>(null);
  const [rfInstance, setRfInstance] = useState<any | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(
    [] as Node<OrgUnitNodeData>[]
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);
  const { theme, systemTheme } = useTheme();
  const [allNodes, setAllNodes] = useState<Node<OrgUnitNodeData>[]>([]);
  const [allEdges, setAllEdges] = useState<Edge[]>([]);
  const [userMap, setUserMap] = useState<Map<string, AdminUser>>(new Map());
  const [membersMap, setMembersMap] = useState<Map<string, TreeMember[]>>(new Map());
  const [membersLoading, setMembersLoading] = useState(false);

  // Fetch org units for specific hierarchy or all units
  const {
    data: unitsForHierarchy = [],
    isLoading: hierarchyUnitsLoading,
    error: hierarchyUnitsError,
  } = useListOrgUnits(hierarchyId || null);

  const {
    data: allUnitsData = [],
    isLoading: allUnitsLoading,
    error: allUnitsError,
  } = useAllOrgUnits();

  // Use hierarchy-specific units if hierarchyId is provided, otherwise use all units
  const units = hierarchyId ? unitsForHierarchy : allUnitsData;
  const unitsLoading = hierarchyId ? hierarchyUnitsLoading : allUnitsLoading;
  const unitsError = hierarchyId ? hierarchyUnitsError : allUnitsError;

  if (unitsError) console.warn(unitsError);

  // Fetch users using React Query hook
  const {
    data: usersResponse,
    isLoading: usersLoading,
    error: usersError,
  } = useListUsers(0, 1000);

  if (usersError) console.warn(usersError);

  // Build user map when users load
  useEffect(() => {
    if (usersResponse?.items) {
      const map = new Map<string, AdminUser>();
      usersResponse.items.forEach((user: AdminUser) => {
        map.set(user.id, user);
      });
      setUserMap(map);
    }
  }, [usersResponse]);

  // Fetch members for all units
  useEffect(() => {
    if (!units || units.length === 0) return;

    const fetchAllMembers = async () => {
      setMembersLoading(true);
      try {
        const newMembersMap = new Map<string, TreeMember[]>();

        // Fetch members for each unit in parallel
        const memberPromises = units.map((unit) =>
          apiClient
            .get<{ items: OrgMember[] }>(`/org/units/${unit.id}/members?skip=0&limit=100`)
            .then((response) => ({
              unitId: unit.id,
              members: (response.data.items || []).map((m) => ({
                ...m,
                user: userMap.get(m.user_id),
              })),
            }))
            .catch(() => ({
              unitId: unit.id,
              members: [],
            }))
        );

        const results = await Promise.all(memberPromises);
        results.forEach(({ unitId, members }) => {
          newMembersMap.set(unitId, members);
        });

        setMembersMap(newMembersMap);
      } catch (error) {
        console.error('Failed to fetch members:', error);
      } finally {
        setMembersLoading(false);
      }
    };

    fetchAllMembers();
  }, [units, userMap]);

  // Build and layout the tree when units, users, or members change
  useEffect(() => {
    if (!units || units.length === 0) return;

    const tree = buildOrgTree(units, userMap, membersMap);
    const { nodes: initialNodes, edges: initialEdges } = createNodesAndEdges(tree);
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      initialNodes,
      initialEdges
    );

    setAllNodes(layoutedNodes);
    setAllEdges(layoutedEdges);
  }, [units, userMap, membersMap]);

  // Get all descendants to collapse recursively
  const getDescendantsToCollapse = (nodeId: string): Set<string> => {
    const descendants = new Set<string>();
    const stack = [nodeId];

    while (stack.length > 0) {
      const current = stack.pop()!;
      const node = allNodes.find((n) => n.id === current);
      if (!node) continue;

      const treeNode = node.data?.node;
      if (isTreeOrgUnit(treeNode) && treeNode.children) {
        treeNode.children.forEach((child) => {
          descendants.add(child.id);
          stack.push(child.id);
        });
      }
    }

    return descendants;
  };

  // Toggle collapse state for a node
  const handleToggleCollapse = (unitId: string) => {
    const newCollapsed = new Set(collapsedNodes);
    if (newCollapsed.has(unitId)) {
      newCollapsed.delete(unitId);
      // Also uncollapse all descendants
      const descendants = getDescendantsToCollapse(unitId);
      descendants.forEach((d) => newCollapsed.delete(d));
    } else {
      newCollapsed.add(unitId);
      // Also collapse all descendants
      const descendants = getDescendantsToCollapse(unitId);
      descendants.forEach((d) => newCollapsed.add(d));
    }
    setCollapsedNodes(newCollapsed);
  };

  // Get all descendants of a collapsed node
  const getDescendants = (nodeId: string): Set<string> => {
    const descendants = new Set<string>();
    const stack = [nodeId];

    while (stack.length > 0) {
      const current = stack.pop()!;
      const childEdges = allEdges.filter((e) => e.source === current);
      childEdges.forEach((edge) => {
        descendants.add(edge.target);
        stack.push(edge.target);
      });
    }

    return descendants;
  };

  // Filter nodes and edges based on collapse state
  useEffect(() => {
    const visibleNodes = new Set<string>();
    const hiddenDescendants = new Set<string>();

    // Find all descendants of collapsed nodes
    collapsedNodes.forEach((nodeId) => {
      const descendants = getDescendants(nodeId);
      descendants.forEach((d) => hiddenDescendants.add(d));
    });

    // All non-hidden nodes are visible
    allNodes.forEach((node) => {
      if (!hiddenDescendants.has(node.id)) {
        visibleNodes.add(node.id);
      }
    });

    // Update nodes with collapse state and visibility
    const filteredNodes = allNodes
      .map((node) => ({
        ...node,
        hidden: !visibleNodes.has(node.id),
        data: {
          ...node.data,
          isCollapsed: collapsedNodes.has(node.id),
          onToggleCollapse: handleToggleCollapse,
          onUnitClick: (unit: TreeOrgUnit) => {
            setSelectedUnit(unit);
          },
        },
      }))
      .filter((node) => !node.hidden);

    // Only show edges where both source and target are visible
    const filteredEdges = allEdges.filter(
      (edge) => visibleNodes.has(edge.source) && visibleNodes.has(edge.target)
    );

    // Re-run dagre layout for the visible set
    try {
      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
        filteredNodes,
        filteredEdges
      );
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
      // Auto-fit and slightly delay to let React Flow update DOM
      if (rfInstance) {
        setTimeout(() => {
          try {
            rfInstance.fitView({ padding: 0.5 });
          } catch (e) {
            console.warn('fitView failed', e);
          }
        }, 60);
      }
    } catch (err) {
      // Fallback to previous behaviour if layout fails
      console.error('Failed to layout nodes after collapse change:', err);
      setNodes(filteredNodes);
      setEdges(filteredEdges);
    }
  }, [collapsedNodes, allNodes, allEdges, setNodes, setEdges, rfInstance]);

  // Determine the actual theme being used
  const currentTheme = theme === 'system' ? systemTheme : theme;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <div className="flex items-center justify-between p-4">
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCollapsedNodes(new Set())}
          >
            Expand All
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const allCollapsible = allNodes
                .filter((node) => {
                  const treeNode = node.data?.node;
                  return isTreeOrgUnit(treeNode) && treeNode.children && treeNode.children.length > 0;
                })
                .map((node) => node.id);
              setCollapsedNodes(new Set(allCollapsible));
            }}
          >
            Collapse All
          </Button>
        </div>
      </div>

      {unitsLoading || usersLoading ? (
        <div className="flex-1 flex items-center justify-center">
          Loading organization structure...
        </div>
      ) : nodes.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          No organizational units found
        </div>
      ) : (
        <div className="flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onInit={(instance: any) => setRfInstance(instance)}
            nodeTypes={nodeTypes}
            colorMode={(currentTheme as 'light' | 'dark' | 'system') || 'light'}
            fitView
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: false,
              style: { stroke: '#374151' },
              markerEnd: {
                type: MarkerType.Arrow,
              },
            }}
            className="bg-background"
            nodesConnectable={false}
            nodesDraggable={false}
          >
            <Background />
            <Controls />
            <MiniMap />
          </ReactFlow>
        </div>
      )}

      {/* Unit Details Modal */}
      <Dialog
        open={!!selectedUnit}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedUnit(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px] max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedUnit?.name}</DialogTitle>
            <DialogDescription>Organizational Unit Details</DialogDescription>
          </DialogHeader>
          {selectedUnit && (
            <div className="space-y-4">
              {/* Unit Information */}
              <div>
                <h3 className="font-semibold text-base mb-3 text-foreground">
                  Unit Information
                </h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">
                      Unit ID
                    </p>
                    <p className="text-sm font-mono">{selectedUnit.id}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">
                      Type
                    </p>
                    <p className="text-sm capitalize">{selectedUnit.unit_type}</p>
                  </div>
                  {selectedUnit.description && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">
                        Description
                      </p>
                      <p className="text-sm">{selectedUnit.description}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">
                      Path
                    </p>
                    <p className="text-sm break-all">
                      {selectedUnit.materialized_path}
                    </p>
                  </div>
                </div>
              </div>

              {/* Manager Information */}
              {selectedUnit.manager ? (
                <div>
                  <h3 className="font-semibold text-base mb-3 text-foreground">
                    Manager
                  </h3>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">
                        Name
                      </p>
                      <p className="text-sm font-semibold">
                        {`${selectedUnit.manager.first_name} ${selectedUnit.manager.last_name}`}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">
                        Email
                      </p>
                      <p className="text-sm break-all">
                        {selectedUnit.manager.email}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">
                        Status
                      </p>
                      <p className="text-sm capitalize">
                        {selectedUnit.manager.status}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground italic">
                    No manager assigned
                  </p>
                </div>
              )}

              {/* Sub-units Information */}
              {selectedUnit.children && selectedUnit.children.length > 0 && (
                <div>
                  <h3 className="font-semibold text-base mb-3 text-foreground">
                    Sub-units ({selectedUnit.children.length})
                  </h3>
                  <div className="space-y-2">
                    {selectedUnit.children.map((child) => (
                      <div
                        key={child.id}
                        className="p-3 bg-muted rounded text-sm"
                      >
                        <p className="font-medium">{child.name}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="capitalize">{child.unit_type}</span>
                          {child.members && child.members.length > 0 && (
                            <>
                              <span>•</span>
                              <span>{child.members.length} member{child.members.length !== 1 ? 's' : ''}</span>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Members Information */}
              {selectedUnit.members && selectedUnit.members.length > 0 && (
                <div>
                  <h3 className="font-semibold text-base mb-3 text-foreground">
                    Members ({selectedUnit.members.length})
                  </h3>
                  <div className="space-y-2">
                    {selectedUnit.members.map((member: TreeMember) => (
                      <div
                        key={member.id}
                        className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded text-sm"
                      >
                        <p className="font-medium text-blue-900 dark:text-blue-100">
                          {member.user ? `${member.user.first_name} ${member.user.last_name}` : member.user_id}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-blue-700 dark:text-blue-300">
                          <span className="capitalize">{member.membership_type}</span>
                          {member.user?.email && (
                            <>
                              <span>•</span>
                              <span>{member.user.email}</span>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
